/**
 * Chunked, resumable upload client — M6 §1 (sessions/parts/finalize) and §5 (the wizard).
 *
 * The shape of a transfer is fixed by the API contract:
 *
 *   POST /uploads                    → {id, part_size, parts, max_bytes}
 *   PUT  /uploads/{id}/parts/{n}     raw bytes + X-Part-Sha256 → {parts, bytes}
 *   POST /uploads/{id}/finalize      {sha256 of the whole file} → UploadRow
 *
 * Three decisions are worth the words:
 *
 * **Sequential parts, not parallel.** The server appends part `n` to a staging file and
 * answers `parts: n+1`; that counter is also what a resume starts from. Parallel PUTs
 * would have to arrive in order anyway (the server answers 409 out of order), so the only
 * thing concurrency would buy here is a reordering bug.
 *
 * **The whole-file sha is computed incrementally, from the part bytes we already read.**
 * WebCrypto has no streaming digest — `crypto.subtle.digest` takes one buffer — so hashing
 * a 10 GB file with it would mean holding 10 GB in memory, and the alternative of reading
 * the file a second time doubles the I/O for no benefit. So `Sha256Stream` below is a small
 * pure-JS FIPS 180-4 implementation fed each part as it is read: one pass over the file,
 * one part in memory at a time. Per-part digests still go through `crypto.subtle` where it
 * exists — one part is a bounded buffer, and the platform implementation is faster.
 *
 * **Pause is an abort; resume is `resumeUpload`.** There is no paused socket to hold: the
 * signal aborts the in-flight part, the server keeps the parts it accepted, and
 * `resumeUpload(id, file)` reads `GET /uploads/{id}` and carries on from `parts`.
 */
import {
  ApiError,
  createUpload,
  fetchUploadById,
  finalizeUpload,
  putPart,
  saveUploadMetadata,
  type UploadMetadata,
  type UploadRow,
  type UploadSession,
} from './api';

/* ------------------------------------------------------------- SHA-256 (FIPS 180-4) */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

/** How much of a part the streaming hash chews through before it yields (see `updateYielding`). */
export const HASH_SLICE_BYTES = 4 * 1024 * 1024;

const rotr = (x: number, n: number) => ((x >>> n) | (x << (32 - n))) >>> 0;

/**
 * An incremental SHA-256 — `update()` as many times as there are parts, `hex()` once.
 *
 * Deliberately not a generic crypto utility: it exists because WebCrypto cannot stream (see
 * the module docstring), and it is verified against `crypto.subtle` in `upload.test.ts`.
 */
export class Sha256Stream {
  private readonly h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  private readonly w = new Uint32Array(64);
  private readonly tail = new Uint8Array(64);
  private tailLen = 0;
  /** Total bytes consumed — becomes the 64-bit length in the padding block. */
  private total = 0;
  private done = false;

  update(bytes: Uint8Array): this {
    if (this.done) throw new Error('Sha256Stream: update after hex()');
    this.total += bytes.length;
    let i = 0;
    if (this.tailLen > 0) {
      const take = Math.min(64 - this.tailLen, bytes.length);
      this.tail.set(bytes.subarray(0, take), this.tailLen);
      this.tailLen += take;
      i = take;
      if (this.tailLen === 64) {
        this.block(this.tail, 0);
        this.tailLen = 0;
      }
    }
    for (; i + 64 <= bytes.length; i += 64) this.block(bytes, i);
    if (i < bytes.length) {
      this.tail.set(bytes.subarray(i), 0);
      this.tailLen = bytes.length - i;
    }
    return this;
  }

  /**
   * `update()` in slices, yielding to the event loop between them.
   *
   * The compression function is pure arithmetic and runs to completion: hashing a 100 MiB
   * part in one call blocks the main thread for long enough to freeze the progress bar, the
   * pause button and every other interaction on the page — precisely while the reader is
   * most likely to reach for Pause. Four MiB is small enough that no single turn is felt and
   * large enough that the yields (one per slice, ~25 per part) cost nothing measurable.
   *
   * `setTimeout(0)` rather than a microtask: a resolved promise would run before the browser
   * gets a chance to paint or to deliver a click, which is the whole point of yielding.
   */
  async updateYielding(bytes: Uint8Array, sliceBytes = HASH_SLICE_BYTES): Promise<this> {
    for (let i = 0; i < bytes.length; i += sliceBytes) {
      this.update(bytes.subarray(i, Math.min(i + sliceBytes, bytes.length)));
      if (i + sliceBytes < bytes.length) await new Promise((resolve) => setTimeout(resolve));
    }
    return this;
  }

  hex(): string {
    if (!this.done) {
      const len = this.total;
      // The padding block(s): 0x80, zeros to 56 mod 64, then the length in bits, big-endian.
      const pad = new Uint8Array(this.tailLen < 56 ? 64 : 128);
      pad.set(this.tail.subarray(0, this.tailLen), 0);
      pad[this.tailLen] = 0x80;
      const view = new DataView(pad.buffer);
      // `len * 8` overflows 32 bits well before 10 GB, so the two halves are computed apart.
      view.setUint32(pad.length - 8, Math.floor(len / 0x20000000), false);
      view.setUint32(pad.length - 4, (len % 0x20000000) * 8, false);
      for (let i = 0; i < pad.length; i += 64) this.block(pad, i);
      this.tailLen = 0;
      this.done = true;
    }
    let out = '';
    for (let i = 0; i < 8; i += 1) out += this.h[i]!.toString(16).padStart(8, '0');
    return out;
  }

  /**
   * One 64-byte block. The `!`s are the codebase's usual answer to
   * `noUncheckedIndexedAccess` on a fixed-length typed array (see `lib/symbology.ts`): every
   * index here is a compile-time constant range over a `Uint32Array(64)` or a slice the
   * caller has already bounded, so a bounds check per access would cost speed for nothing.
   */
  private block(p: Uint8Array, off: number): void {
    const w = this.w;
    for (let i = 0; i < 16; i += 1) {
      const j = off + i * 4;
      w[i] = ((p[j]! << 24) | (p[j + 1]! << 16) | (p[j + 2]! << 8) | p[j + 3]!) >>> 0;
    }
    for (let i = 16; i < 64; i += 1) {
      const s0 = (rotr(w[i - 15]!, 7) ^ rotr(w[i - 15]!, 18) ^ (w[i - 15]! >>> 3)) >>> 0;
      const s1 = (rotr(w[i - 2]!, 17) ^ rotr(w[i - 2]!, 19) ^ (w[i - 2]! >>> 10)) >>> 0;
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, h] = this.h as unknown as [
      number, number, number, number, number, number, number, number,
    ];
    for (let i = 0; i < 64; i += 1) {
      const S1 = (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) >>> 0;
      const ch = ((e & f) ^ (~e & g)) >>> 0;
      const t1 = (h + S1 + ch + K[i]! + w[i]!) >>> 0;
      const S0 = (rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) >>> 0;
      const maj = ((a & b) ^ (a & c) ^ (b & c)) >>> 0;
      const t2 = (S0 + maj) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    const s = [a, b, c, d, e, f, g, h];
    for (let i = 0; i < 8; i += 1) this.h[i] = (this.h[i]! + s[i]!) >>> 0;
  }
}

const toHex = (buf: ArrayBuffer): string => {
  let out = '';
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i += 1) out += bytes[i]!.toString(16).padStart(2, '0');
  return out;
};

/**
 * SHA-256 of one bounded buffer (a part), hex-encoded.
 *
 * `crypto.subtle` where the browser exposes it — it is native and a part is at most
 * `part_size` — and the pure implementation otherwise, so a non-secure origin or a test
 * environment without WebCrypto still produces the same digest rather than failing.
 */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  // `bytes` is always a fresh view over a buffer this module just read out of a Blob, so it
  // is handed to `digest` as it stands — copying it first would mean a second 100 MiB per
  // part for nothing. The cast is only about `Uint8Array`'s buffer parameter: `BufferSource`
  // names `ArrayBuffer`, the view's is `ArrayBufferLike`, and a `SharedArrayBuffer` cannot
  // reach here (a Blob read never produces one).
  if (subtle) return toHex(await subtle.digest('SHA-256', bytes as unknown as BufferSource));
  return new Sha256Stream().update(bytes).hex();
}

/* ------------------------------------------------------------------- the transfer */

export interface UploadProgress {
  /** Bytes the server has accepted so far. */
  sent: number;
  /** The file's size, so a caller never has to keep it. */
  total: number;
  /** Parts accepted so far. */
  parts: number;
  /** `sent / total`, clamped to 0…1 — what the progress bar wants. */
  ratio: number;
}

export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  /** Aborting is how the wizard pauses **and** how it cancels; `resumeUpload` carries on. */
  signal?: AbortSignal;
  /** "This is a new version of…" — the dataset the upload will republish. */
  datasetId?: string | null;
  /** A hint at the payload family when the extension is ambiguous (`spatial`/`tabular`). */
  kindHint?: string | null;
  /**
   * Who is submitting (client, 2026-09-18). The server requires both on `POST /uploads`,
   * so they travel with the create call rather than waiting for the metadata save.
   */
  submitterName?: string | null;
  submitterEmail?: string | null;
  /**
   * Metadata to save against the session **before its first byte** (`POST
   * /uploads/{id}/metadata`).
   *
   * The wizard does not use this — it saves its form on every step change, because the
   * reader is still editing it — but a one-shot submitter has nothing to edit: the manual
   * indicator values panel builds a CSV and its `{indicator_id, period, level, country}`
   * in the same click.
   *
   * **The timing is the whole point, and it is not obvious.** `POST /uploads/{id}/finalize`
   * does not merely close the session: it enqueues the checks inside the same request
   * (`apps.datasets.views_uploads`), and an `indicator_values` submission's checks are
   * *about* this metadata — `submissions.validate` reads `upload.metadata` to learn which
   * indicator, period and level the file claims to be. Saved a moment later, after the
   * finalize answered, every submission would be validated against `{}` and land in the
   * queue as "Issues detected" for a file that was never wrong. So it goes up as soon as
   * there is a session id to address, and a refusal there fails the upload before a byte
   * moves: a submission whose indicator and period never arrived is not a submission.
   */
  metadata?: UploadMetadata | null;
  /**
   * Backoff before retry `n` (1-based). Injectable so the tests can assert the retry
   * *policy* without waiting out the real delays.
   */
  backoffMs?: (attempt: number) => number;
}

export interface UploadHandle {
  /**
   * The session id, available as soon as the session exists — long before the transfer
   * finishes. The wizard needs it early: it is what a pause/resume and a cancel address.
   */
  id: Promise<number>;
  /** The finalized `UploadRow` (status `pending`, checks queued), or a rejection. */
  done: Promise<UploadRow>;
}

/** One try plus three retries, per the plan ("retry 3× with backoff on 5xx/network"). */
export const MAX_RETRIES = 3;

const defaultBackoff = (attempt: number) => Math.min(8000, 400 * 2 ** (attempt - 1));

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isAbort = (err: unknown): boolean =>
  err instanceof DOMException ? err.name === 'AbortError' : (err as Error)?.name === 'AbortError';

/**
 * Retryable failures are the ones a second try could plausibly fix: a transport error
 * (`TypeError` from `fetch`) and a 5xx. A 4xx is a statement about the request — a bad
 * checksum, a part out of order, a file over the cap — and repeating it only wastes the
 * user's bandwidth.
 */
const isRetryable = (err: unknown): boolean => {
  if (isAbort(err)) return false;
  if (err instanceof ApiError) return err.status >= 500;
  return true;
};

function abortError(): DOMException {
  return new DOMException('Upload aborted', 'AbortError');
}

async function withRetry<T>(
  work: () => Promise<T>,
  opts: UploadOptions,
): Promise<T> {
  const backoff = opts.backoffMs ?? defaultBackoff;
  let lastError: unknown;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    if (opts.signal?.aborted) throw abortError();
    try {
      return await work();
    } catch (err) {
      lastError = err;
      if (!isRetryable(err) || attempt === MAX_RETRIES) throw err;
      await sleep(backoff(attempt + 1));
    }
  }
  throw lastError;
}

/**
 * The bytes of one slice.
 *
 * `Blob.arrayBuffer()` where it exists, `FileReader` otherwise: the method is missing on
 * older Safari and on jsdom's `Blob`, and a reader that only works on the newest browsers
 * would fail on exactly the machines a 10 GB upload is most likely to be attempted from.
 */
async function readBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') return new Uint8Array(await blob.arrayBuffer());
  const buf = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsArrayBuffer(blob);
  });
  return new Uint8Array(buf);
}

/** How many `part_size` slices a file of `size` bytes makes (an empty file still makes 0). */
export const partCount = (size: number, partSize: number): number =>
  partSize > 0 ? Math.ceil(size / partSize) : 0;

/**
 * Send every part from `from` onwards, feeding the whole-file hash as it goes, and finalize.
 *
 * Parts before `from` are still *read* (never sent): they are already on the server, but
 * their bytes are part of the file's digest, and re-reading them locally is far cheaper
 * than asking the server for them.
 */
async function transfer(
  session: UploadSession,
  file: File,
  from: number,
  opts: UploadOptions,
): Promise<UploadRow> {
  const partSize = session.part_size;
  const total = file.size;
  const count = partCount(total, partSize);
  const whole = new Sha256Stream();
  let sent = Math.min(from * partSize, total);

  for (let n = 0; n < count; n += 1) {
    if (opts.signal?.aborted) throw abortError();
    const slice = file.slice(n * partSize, Math.min((n + 1) * partSize, total));
    const bytes = await readBytes(slice);
    await whole.updateYielding(bytes);
    if (n < from) continue;
    const sha = await sha256Hex(bytes);
    const res = await withRetry(
      () => putPart(session.id, n, bytes, sha, { signal: opts.signal }),
      opts,
    );
    sent = Math.min(total, sent + bytes.length);
    opts.onProgress?.({
      sent,
      total,
      parts: res.parts ?? n + 1,
      ratio: total > 0 ? Math.min(1, sent / total) : 1,
    });
  }

  if (opts.signal?.aborted) throw abortError();
  return withRetry(() => finalizeUpload(session.id, whole.hex()), opts);
}

/**
 * Open a session for `file` and upload it, part by part.
 *
 * Returns immediately with two promises rather than awaiting the session: the wizard shows
 * the file card and the progress bar the moment the user drops a file, and it needs the id
 * to wire pause/cancel long before `done` settles.
 */
export function startUpload(file: File, opts: UploadOptions = {}): UploadHandle {
  let resolveId: (id: number) => void = () => {};
  let rejectId: (err: unknown) => void = () => {};
  const id = new Promise<number>((resolve, reject) => {
    resolveId = resolve;
    rejectId = reject;
  });

  const done = (async () => {
    let session: UploadSession;
    try {
      session = await withRetry(
        () =>
          createUpload({
            filename: file.name,
            size_bytes: file.size,
            kind_hint: opts.kindHint ?? null,
            dataset_id: opts.datasetId ?? null,
            submitter_name: opts.submitterName ?? null,
            submitter_email: opts.submitterEmail ?? null,
          }),
        opts,
      );
    } catch (err) {
      rejectId(err);
      throw err;
    }
    resolveId(session.id);
    // Before the first part, never after the finalize — see `UploadOptions.metadata`.
    if (opts.metadata) {
      await withRetry(() => saveUploadMetadata(session.id, opts.metadata as UploadMetadata), opts);
    }
    return transfer(session, file, session.parts ?? 0, opts);
  })();

  // The id promise is settled by `done` either way; an unobserved rejection there must not
  // become an unhandled rejection of its own.
  void id.catch(() => {});
  return { id, done };
}

/**
 * Continue an interrupted upload: read the session, then send from `parts` onwards.
 *
 * The same `file` must be handed back — the browser cannot re-open the one the user picked
 * — and the server verifies the whole-file sha at finalize, so a different file is caught
 * there rather than silently stitched onto someone else's parts.
 */
export function resumeUpload(uploadId: number, file: File, opts: UploadOptions = {}): UploadHandle {
  const done = (async () => {
    const session = await withRetry(() => fetchUploadById(uploadId), opts);
    opts.onProgress?.({
      sent: Math.min(session.bytes_received ?? 0, file.size),
      total: file.size,
      parts: session.parts ?? 0,
      ratio: file.size > 0 ? Math.min(1, (session.bytes_received ?? 0) / file.size) : 1,
    });
    return transfer(session, file, session.parts ?? 0, opts);
  })();
  return { id: Promise.resolve(uploadId), done };
}
